import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { bool, string } from 'prop-types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ApiImg, Icon, Skeleton, useTravelPurchaseContext } from 'components';
import { formatDate, formatLocalizedCurrency } from 'helpers';

const TravelPurchaseSummary = ({ isValidationStep = false, className = '', skeletonClassName = '' }) => {
  const { t } = useTranslation();

  const { travel, travelLoading, travelDepartureLoading } = useTravelPurchaseContext();
  const currentStepOrder = useSelector((state) => state.travelPurchase.steps.find((step) => step.current)?.order);
  const passengersCategories = useSelector((state) => state.travelPurchase.passengersCat);
  const storedDepartureDate = useSelector((state) => state.travelPurchase.departureDate);
  const storedReturnDate = useSelector((state) => state.travelPurchase.returnDate);
  const departureCity = useSelector((state) => state.travelPurchase.departureCity);
  const duration = useSelector((state) => state.travelPurchase.duration);
  const lodging = useSelector((state) => state.travelPurchase.lodging);
  const storedInsurance = useSelector((state) => state.travelPurchase.insurance);
  const storedSubvention = useSelector((state) => state.travelPurchase.subvention);
  const storedParticipants = useSelector((state) => state.travelPurchase.participants);
  const storedDepositAmount = useSelector((state) => state.travelPurchase.depositAmount);

  const totalPassengers = useMemo(() => {
    let total = '';

    passengersCategories.forEach((passenger) => {
      if (passenger.qty > 0) {
        total += `${passenger.qty} ${passenger.label}${passenger.qty > 1 ? 's' : ''}, `;
      }
    });

    // Remove last char (',')
    return total.slice(0, -2);
  }, [passengersCategories]);

  const insurancePrice = useMemo(() => {
    if (!storedInsurance?.unitPrice) return formatLocalizedCurrency(0);
    // Convert insurance price from the store from cents to euros
    let price = storedInsurance.unitPrice / 100;
    if (storedInsurance.scope === 'Personne') {
      price *= storedParticipants.length;
    }
    return formatLocalizedCurrency(price);
  }, [storedInsurance, storedParticipants]);

  const totalPriceTTC = useMemo(() => {
    let total = travel?.priceNotC2C || 0;

    if (lodging?.price) {
      total = lodging.price;
    }

    if (storedInsurance?.unitPrice && storedParticipants?.length > 0) {
      if (storedInsurance.scope === 'Personne') {
        total += (storedInsurance.unitPrice / 100) * storedParticipants.length;
      } else {
        total += storedInsurance.unitPrice / 100;
      }
    }

    if (storedSubvention) {
      total -= storedSubvention / 100;
    }

    return total;
  }, [travel, storedInsurance, lodging, storedSubvention, storedParticipants]);

  const balance = useMemo(() => {
    return totalPriceTTC - storedDepositAmount;
  }, [totalPriceTTC, storedDepositAmount]);

  return (
    <>
      {(travelDepartureLoading || travelLoading) && (
        <div className={clsx(skeletonClassName)}>
          <Skeleton classNames="hidden lg:block lg:w-full lg:h-[37rem] lg:rounded-xl" />
          <Skeleton classNames="fixed bottom-2 bg-gray-100 left-0 w-full h-24 lg:hidden" />
        </div>
      )}

      {!travelDepartureLoading && !travelLoading && !isEmpty(travel) && (
        <div className={clsx('travel-purchase-summary h-auto rounded-xl bg-white mb-4', className)}>
          <div className={clsx(!isValidationStep && 'hidden lg:flex lg:flex-col')}>
            <div className={clsx('rounded-t-xl shrink-0 overflow-hidden', isValidationStep && 'hidden lg:block')}>
              <ApiImg src={travel?.vignette} width="370" height="200" />
            </div>

            {/* TRAVEL INFORMATIONS */}
            <div className="p-6 flex flex-col gap-3">
              <h3 className="uppercase font-semibold">{travel?.title}</h3>

              <p className="uppercase">
                <Icon name="location-dot" iconStyle="fad" classes="text-yellow-500 mr-4" />
                {travel?.city} - {travel?.country}
              </p>

              {departureCity?.label && (
                <p>
                  <Icon name="plane-departure" iconStyle="fad" classes="text-yellow-500 mr-2" />
                  {departureCity.label}
                </p>
              )}

              {totalPassengers && (
                <p>
                  <Icon name="user-group" iconStyle="fad" classes="text-yellow-500 mr-2" />
                  {totalPassengers}
                </p>
              )}

              {storedDepartureDate && storedReturnDate && (
                <>
                  <p>{t('purchase:departDate', { date: formatDate(storedDepartureDate) })}</p>
                  <p>{t('purchase:backDate', { date: formatDate(storedReturnDate) })}</p>
                </>
              )}
              {duration?.label && <p>{duration.label}</p>}
              {lodging?.label && currentStepOrder > 1 && <p>{lodging.label}</p>}
            </div>
          </div>

          {/* PRICES */}
          <div className={clsx('prices-container', isValidationStep && 'validation-step')}>
            {currentStepOrder <= 1 && (
              <>
                {/* TRAVEL BASE PRICE */}
                <div className="flex justify-between">
                  <p>{t('fromPrice')} </p>
                  <p className="font-bold">
                    {formatLocalizedCurrency(travel?.priceNotC2C)}
                    <span>{t('unitPrice')}</span>
                  </p>
                </div>
              </>
            )}

            {currentStepOrder > 1 && (
              <>
                {lodging?.price && storedInsurance && (
                  <div className="flex justify-between mb-4">
                    <p>{t('purchase:travel')} </p>
                    <p className="font-bold">{formatLocalizedCurrency(lodging?.price)}</p>
                  </div>
                )}
                {storedSubvention > 0 && (
                  <div className="flex justify-between mb-4">
                    <p>{t('purchase:subvention.label')} </p>
                    <p className="font-bold">-{formatLocalizedCurrency(storedSubvention / 100)}</p>
                  </div>
                )}
                {currentStepOrder > 2 && storedInsurance && (
                  <div className="flex justify-between mb-4">
                    <p>{t('purchase:stepper.insurance')}</p>
                    <p className="font-bold">{insurancePrice}</p>
                  </div>
                )}
                <div className={clsx('flex justify-between', storedDepositAmount && 'mb-4')}>
                  <p>{t('purchase:totalPriceTTC')} </p>
                  <p className="font-bold">{formatLocalizedCurrency(totalPriceTTC)}</p>
                </div>
                {currentStepOrder > 4 && storedDepositAmount && (
                  <>
                    <div className="flex justify-between mb-4">
                      <p>{t('purchase:deposit')} </p>
                      <p className="font-bold">{formatLocalizedCurrency(storedDepositAmount)}</p>
                    </div>
                    <div className="flex justify-between">
                      <p>{t('purchase:balance.balanceAfterDeposit')} </p>
                      <p className="font-bold">{formatLocalizedCurrency(balance)}</p>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

TravelPurchaseSummary.propTypes = {
  isValidationStep: bool,
  className: string,
  skeletonClassName: string
};

export default TravelPurchaseSummary;
